import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import * as S from 'pages/Home/AnnouncementSlideContent.styled';

import { Breakpoints } from 'utils/const.styles';

import { faCopy } from '@fortawesome/pro-regular-svg-icons';

import VideoPreviewModal from './RecurringPage/VideoPreviewModal';

const AnnouncementSlideContent = ({ slide, height, width }) => {
	const {
		desktopBGImage,
		mobileBGImage,
		header,
		subheader,
		content,
		actionLabel,
		actionUrl,
		actionType,
		actionContent,
		type,
		showLearnMore,
		videoUrl
	} = slide;

	const isCopyableButton = actionType === 'copy';

	const { t } = useTranslation();
	const { showInfo } = useAlert();
	const { LanguageCode } = useLocale();
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const handleCopy = () => {
		navigator.clipboard.writeText(actionContent);
		showInfo({
			header: 'copiedToClipboard'
		});
	};

	const navigate = useNavigate();

	const handleButtonClick = () => {
		switch (actionType) {
			case 'copy':
				handleCopy();
				return;
			case 'navigateInNewTab':
				window.open(actionUrl, '_blank', 'noopener,noreferrer');
				return;
			case 'navigate':
				navigate(actionUrl);
				return;
			default:
				return;
		}
	};

	return (
		<S.AnnouncementSlideContent
			backgroundImage={
				isMobile
					? JSON.parse(mobileBGImage)[LanguageCode] || JSON.parse(mobileBGImage).engValue
					: JSON.parse(desktopBGImage)[LanguageCode] || JSON.parse(desktopBGImage).engValue
			}
			onClick={() =>
				NiceModal.show(VideoPreviewModal, {
					videoUrl,
					header,
					content,
					actionUrl,
					actionLabel
				})
			}
			isClickable={videoUrl}
			height={height}
			width={width}>
			<S.SlideContent withTag={type}>
				{type && (
					<S.CornerTag>
						<Typography variant="caption" fontWeight="500" color="var(--alwaysWhiteColor)">
							{type}
						</Typography>
					</S.CornerTag>
				)}
				<Typography variant="h4" fontWeight="700" color="white" noWrap gutterBottom>
					{typeof header === 'string' ? t(header) : header}
				</Typography>
				{subheader && (
					<Typography variant="p3" fontWeight="500" color="white" gutterBottom noWrap className="pb-3">
						{typeof subheader === 'string' ? t(subheader) : subheader}
					</Typography>
				)}
				<Typography
					variant="p3"
					color="white"
					gutterBottom
					noWrap
					css={css`
						line-height: var(--paragraph3-line-height);
					`}>
					{typeof content === 'string' ? t(content) : content}
				</Typography>
				<S.ButtonsWrapper>
					{actionLabel && (
						<S.WhiteButton
							variant="whiteButton"
							onClick={(e) => {
								handleButtonClick();
								e.stopPropagation();
							}}
							label={
								isCopyableButton ? (
									<Trans
										key={actionLabel}
										values={{
											promocode: actionContent
										}}
									/>
								) : (
									actionLabel
								)
							}>
							{isCopyableButton && <Icon icon={faCopy} />}
						</S.WhiteButton>
					)}
					{showLearnMore && (
						<S.WhiteButton
							variant="outline"
							onClick={() =>
								NiceModal.show(VideoPreviewModal, {
									videoUrl,
									header,
									content,
									actionUrl,
									actionLabel
								})
							}
							label={'learnMore'}
						/>
					)}
				</S.ButtonsWrapper>
			</S.SlideContent>
		</S.AnnouncementSlideContent>
	);
};

export default AnnouncementSlideContent;
